import { create } from "zustand";

export type DayPriceMap = Record<string, number>;

export interface DayPriceStats {
  /** Mean of all loaded prices; `null` when there are none. */
  averagePrice: number | null;
  /** 25th percentile (bottom quartile) of all loaded prices; `null` when there are none. */
  lowPriceThreshold: number | null;
}

/**
 * A day is highlighted as a "good deal" when it is both in the cheapest quartile AND
 * below the average. The average guard keeps the highlight meaningful when prices are
 * clustered/tied (e.g. mostly 28€ with a few 9€), where the quartile alone would otherwise
 * land on the dominant value and flag everything.
 */
const computeDayPriceStats = (prices: DayPriceMap): DayPriceStats => {
  const values = Object.values(prices);
  if (values.length === 0) return { averagePrice: null, lowPriceThreshold: null };

  const averagePrice = values.reduce((sum, value) => sum + value, 0) / values.length;

  const sorted = [...values].sort((a, b) => a - b);
  const rank = (sorted.length - 1) * 0.25;
  const low = Math.floor(rank);
  const high = Math.ceil(rank);
  // Linear interpolation between the two closest ranks.
  const lowPriceThreshold = sorted[low] + (sorted[high] - sorted[low]) * (rank - low);

  return { averagePrice, lowPriceThreshold };
};

export const useDayPriceStore = create<
  {
    /** Prices keyed by date string (YYYY-MM-DD) */
    dayPrices: DayPriceMap;
    /** Whether a daily-price fetch is currently in flight (drives per-day skeletons). */
    isPricesLoading: boolean;
    setDayPrices: (prices: DayPriceMap) => void;
    setPricesLoading: (isPricesLoading: boolean) => void;
  } & DayPriceStats
>((set) => ({
  dayPrices: {},
  averagePrice: null,
  lowPriceThreshold: null,
  isPricesLoading: false,
  setDayPrices: (prices) => set({ dayPrices: prices, ...computeDayPriceStats(prices) }),
  setPricesLoading: (isPricesLoading) => set({ isPricesLoading }),
}));
